import type { StoredSettings } from "./CanvasSettingsState";
import { reactive, toRaw } from "vue";
import CanvasSettingsState from "./CanvasSettingsState";
import {
  DEFAULT_LEGEND,
  DEFAULT_NOTE_CONFIG,
  DEFAULT_PHOTO_NOTE_CONFIG,
} from "../defaultLegend";
import { Facade, DraggerType } from "@/drawing";
import _ from "lodash";
import DraggerMenuState from "./DraggerMenuState";
import ScaleMenuState from "./ScaleMenuState";
import type { NoteConfig } from "@/types/legend.types";
import HatchLibrary from "@/hatches/library";

interface NoteMenuState {
  activeNote: NoteConfig;
  activeCategories: string[];
  noteConfigs: NoteConfig[];
  storedSettings: StoredSettings | null;
  previousNote?: NoteConfig;
  initialize: (storedSettings: StoredSettings | null) => void;
  onDrawingOpen: (userConfigs: NoteConfig[]) => void;
  onLegendChange: (userConfigs: NoteConfig[]) => void;
  computeNoteConfig: (config?: NoteConfig) => NoteConfig;
  getPhotoNoteConfig: () => NoteConfig;
  updateComputedNoteConfig: () => NoteConfig | null;
  setActiveNoteConfig: (
    note: NoteConfig,
    shouldStorePrevious?: boolean
  ) => void;
  persistActiveCategories: () => void;
}

export default reactive<NoteMenuState>({
  activeNote: DEFAULT_NOTE_CONFIG,
  activeCategories: [],
  noteConfigs: DEFAULT_LEGEND.note_configs,
  storedSettings: null,
  initialize(storedSettings: StoredSettings | null) {
    this.storedSettings = storedSettings;
  },
  onDrawingOpen(userConfigs: NoteConfig[]) {
    this.noteConfigs = userConfigs;
    // retrieve stored state from local storage
    if (this.storedSettings?.note?.activeNoteIdx != undefined) {
      this.activeNote = userConfigs[this.storedSettings.note.activeNoteIdx] ?? DEFAULT_NOTE_CONFIG;
    }
    if (this.storedSettings?.note?.previousNoteIdx != undefined) {
      this.previousNote = userConfigs[this.storedSettings.note.previousNoteIdx] ?? DEFAULT_NOTE_CONFIG;
    }
    if (this.storedSettings?.note?.activeCategories) {
      this.activeCategories = this.storedSettings.note.activeCategories;
    }
  },
  onLegendChange(userConfigs: NoteConfig[]) {
    this.noteConfigs = userConfigs;
    this.activeNote = DEFAULT_NOTE_CONFIG;
    this.activeCategories = [];
  },
  // Merge selected default config and scale config to get the full config to apply
  // Do not set any properties the class, only returns the computed config (Raw).
  computeNoteConfig(config?: NoteConfig): NoteConfig {
    // clone active note
    if (config) {
      config = toRaw(config);
    } else if (this.activeNote) {
      config = toRaw(this.activeNote);
    } else {
      config = DEFAULT_NOTE_CONFIG;
    }

    const computedConfig = structuredClone(config);

    // Le moteur ODA est name-keyed (OdDbLayerTable n'a pas de notion d'id) : on résout ici
    // le layerId persisté vers le layername effectif. Nécessaire ici (et pas seulement
    // dans NoteConfigManager.setActive) car le payload de drag-and-drop du canvas
    // (NoteConfigButton.vue -> useCanvasDrop.ts) consomme ce config calculé directement,
    // sans jamais passer par setActive.
    if (computedConfig.props.layerId) {
      const layer = DraggerMenuState.getLayerById(computedConfig.props.layerId);
      computedConfig.props.layername = layer?.name;
    }

    // use active text and scale config
    const scaleConfig = ScaleMenuState.computeActiveScale();
    if (scaleConfig) {
      computedConfig.props = { ...computedConfig.props, ...scaleConfig.props };

      // Compute hatch scale: userScale (saisi par l'utilisateur) sinon la baseScale de la
      // bibliothèque (définie pour textHeight=1), × active textHeight.
      if (computedConfig.hatch) {
        const textHeight =
          scaleConfig.props.textsize ?? scaleConfig.props.textstyle?.size ?? 1;
        const pattern = HatchLibrary.getByName(computedConfig.hatch.name);
        const baseScale = computedConfig.hatch.userScale ?? pattern?.baseScale ?? 1;
        computedConfig.hatch = {
          ...computedConfig.hatch,
          scale: baseScale * textHeight,
        };
      }
    }

    return computedConfig;
  },
  getPhotoNoteConfig(): NoteConfig {
    for (const noteConfig of this.noteConfigs) {
      if (noteConfig.type == "photo") return noteConfig;
    }
    return DEFAULT_PHOTO_NOTE_CONFIG;
  },
  updateComputedNoteConfig(): NoteConfig | null {
    const computedConfig = this.computeNoteConfig();
    if (!computedConfig) return null;
    // Update the note config in the new DrawingJS Facade
    if (Facade.isInitialized) {
      Facade.noteConfig.setActive(toRaw(computedConfig));
    }
    return computedConfig;
  },
  setActiveNoteConfig(note: NoteConfig, shouldStorePrevious = false) {
    if (shouldStorePrevious) {
      this.previousNote = this.activeNote;
      const idx = this.noteConfigs.findIndex(
        (note1) => note1 === this.activeNote
      );
      this.storedSettings = _.set(
        this.storedSettings || {},
        "note.previousNoteIdx",
        idx
      );
    } else {
      this.previousNote = undefined;
      this.storedSettings = _.set(
        this.storedSettings || {},
        "note.previousNoteIdx",
        undefined
      );
    }
    CanvasSettingsState.updateAndPersist();
    const idx = this.noteConfigs.findIndex((note1) => note1 === note);
    this.activeNote = note;
    const config = this.updateComputedNoteConfig();
    this.storedSettings = _.set(
      this.storedSettings || {},
      "note.activeNoteIdx",
      idx
    );
    if (DraggerMenuState.activeDragger.name === DraggerType.Select && config) {
      // Selection property is handled by the new DrawingJS selection system
    }
  },
  persistActiveCategories() {
    this.storedSettings = _.set(
      this.storedSettings || {},
      "note.activeCategories",
      this.activeCategories
    );
    CanvasSettingsState.updateAndPersist();
  },
});
